import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import videoBg from "./home.mp4";
import settingsService from "./services/settingsService";

function HeroSection() {
  const [hero, setHero] = useState({
    badge: "Architecture • Interior Design Studio",
    title_line1: "Transforming",
    title_highlight: "Spaces Into Art",
    description: "Creating elegant interiors and innovative architectural solutions that blend beauty, comfort, and functionality for modern living.",
    cta_primary_text: "Explore Projects",
    cta_primary_link: "#projects",
    cta_secondary_text: "View Packages",
    cta_secondary_link: "#packages",
  });

  useEffect(() => {
    async function loadHeroSettings() {
      try {
        const savedHero = await settingsService.getSetting("hero");
        const allSettings = await settingsService.getAll();

        if (savedHero) {
          setHero((prev) => ({
            ...prev,
            badge: savedHero.badge || prev.badge,
            title_line1: savedHero.title_line1 || (savedHero.headline ? savedHero.headline.split(" ")[0] : prev.title_line1),
            title_highlight: savedHero.title_highlight || (savedHero.headline ? savedHero.headline.split(" ").slice(1).join(" ") : prev.title_highlight),
            description: savedHero.description || savedHero.subtitle || allSettings.hero_description || prev.description,
            cta_primary_text: savedHero.cta_primary_text || savedHero.cta_primary || prev.cta_primary_text,
            cta_primary_link: savedHero.cta_primary_link || "#projects",
            cta_secondary_text: savedHero.cta_secondary_text || savedHero.cta_secondary || prev.cta_secondary_text,
            cta_secondary_link: savedHero.cta_secondary_link || "#packages",
          }));
        } else if (allSettings.hero_headline) {
          setHero((prev) => ({
            ...prev,
            title_line1: allSettings.hero_headline.split(" ")[0] || prev.title_line1,
            title_highlight: allSettings.hero_headline.split(" ").slice(1).join(" ") || prev.title_highlight,
            description: allSettings.hero_description || prev.description,
          }));
        }
      } catch (err) {
        // Fallback safely
      }
    }
    loadHeroSettings();
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden">
      
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={videoBg} type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
        <div className="max-w-4xl">

          <p className="uppercase tracking-[6px] text-[#9BB7A3] font-semibold mb-5 animate-pulse text-xs sm:text-sm">
            {hero.badge}
          </p>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold text-white leading-tight mb-6">
            {hero.title_line1}{" "}
            <span className="block text-[#9BB7A3]">
              {hero.title_highlight}
            </span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-slate-200 max-w-2xl mx-auto leading-relaxed mb-10">
            {hero.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <a 
              href={hero.cta_primary_link || "#projects"} 
              className="px-8 py-4 rounded-full bg-[#9BB7A3] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#86a38e] transition-all duration-300 hover:scale-105 no-underline shadow-lg"
            >
              <span>{hero.cta_primary_text}</span>
              <ArrowRight size={18} />
            </a>

            <a 
              href={hero.cta_secondary_link || "#packages"} 
              className="px-8 py-4 rounded-full border-2 border-white text-white font-bold hover:bg-white hover:text-slate-900 transition-all duration-300 no-underline"
            >
              <span>{hero.cta_secondary_text}</span>
            </a>
          </div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="w-7 h-12 border-2 border-white/60 rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-white rounded-full mt-2 animate-bounce"></div>
        </div>
      </div>

    </section>
  );
}

export default HeroSection;